import { CalendarClock, ExternalLink, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import {
  COLORES_EVENTO,
  rango,
  useBorrarEvento,
  useGuardarEvento,
  type EventoEnarm,
  type EventoPersonal,
} from './queries';

type Color = EventoPersonal['color'];

/** Formulario de un evento personal: nuevo (con la fecha del día elegido) o edición. */
export function EventoDialog({
  evento,
  fecha,
  onClose,
}: {
  evento: EventoPersonal | null;
  fecha: string;
  onClose: () => void;
}) {
  const [titulo, setTitulo] = useState(evento?.title ?? '');
  const [inicio, setInicio] = useState(evento?.date ?? fecha);
  const [fin, setFin] = useState(evento?.endDate ?? '');
  const [hora, setHora] = useState(evento?.time ?? '');
  const [notas, setNotas] = useState(evento?.notes ?? '');
  const [color, setColor] = useState<Color>(evento?.color ?? 'morado');
  const [confirmar, setConfirmar] = useState(false);
  const guardar = useGuardarEvento();
  const borrar = useBorrarEvento();
  const ocupado = guardar.isPending || borrar.isPending;

  const enviar = () => {
    if (titulo.trim() === '') {
      toast.error('Escribe el título del evento.');
      return;
    }
    if (fin !== '' && fin < inicio) {
      toast.error('La fecha de fin no puede ser anterior a la de inicio.');
      return;
    }
    guardar.mutate(
      {
        id: evento?.id,
        body: {
          title: titulo.trim(),
          date: inicio,
          endDate: fin === '' ? null : fin,
          time: hora === '' ? null : hora,
          notes: notas.trim() === '' ? null : notas.trim(),
          color,
        },
      },
      {
        onSuccess: () => {
          toast.success(evento ? 'Evento actualizado.' : 'Evento agregado a tu calendario.');
          onClose();
        },
        onError: (e) => toast.error(mensajeDe(e)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent>
        <DialogTitle>{evento ? 'Editar evento' : 'Nuevo evento'}</DialogTitle>
        <DialogDescription>
          Tus eventos se guardan aparte del plan: si lo reconfiguras, siguen aquí.
        </DialogDescription>
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            enviar();
          }}
        >
          <label className="block text-xs font-bold text-texto-3">
            Título
            <input
              className="input-vidrio mt-1 w-full"
              value={titulo}
              maxLength={120}
              autoFocus
              placeholder="Ej. Guardia, simulacro, cita…"
              onChange={(e) => setTitulo(e.target.value)}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-xs font-bold text-texto-3">
              Fecha
              <input
                type="date"
                required
                className="input-vidrio mt-1 w-full"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
              />
            </label>
            <label className="block text-xs font-bold text-texto-3">
              Hasta (opcional)
              <input
                type="date"
                className="input-vidrio mt-1 w-full"
                value={fin}
                min={inicio}
                onChange={(e) => setFin(e.target.value)}
              />
            </label>
            <label className="block text-xs font-bold text-texto-3">
              Hora (opcional)
              <input
                type="time"
                className="input-vidrio mt-1 w-full"
                value={hora}
                onChange={(e) => setHora(e.target.value)}
              />
            </label>
          </div>
          <fieldset>
            <legend className="text-xs font-bold text-texto-3">Color</legend>
            <div className="mt-1 flex flex-wrap gap-2">
              {(Object.keys(COLORES_EVENTO) as Color[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={COLORES_EVENTO[c].nombre}
                  aria-pressed={color === c}
                  onClick={() => setColor(c)}
                  className={cn(
                    'size-8 rounded-full border-2 border-transparent transition',
                    color === c && 'scale-110 border-white ring-2 ring-cian-500',
                  )}
                  style={{ backgroundColor: COLORES_EVENTO[c].hex }}
                />
              ))}
            </div>
          </fieldset>
          <label className="block text-xs font-bold text-texto-3">
            Notas (opcional)
            <textarea
              className="input-vidrio mt-1 min-h-20 w-full"
              value={notas}
              maxLength={1000}
              onChange={(e) => setNotas(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            {evento ? (
              confirmar ? (
                <button
                  type="button"
                  className="btn-cs btn-peligro"
                  disabled={ocupado}
                  onClick={() =>
                    borrar.mutate(evento.id, {
                      onSuccess: () => {
                        toast.success('Evento borrado.');
                        onClose();
                      },
                      onError: (e) => toast.error(mensajeDe(e)),
                    })
                  }
                >
                  <Trash2 size={16} /> ¿Borrar? Confirmar
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-cs btn-vidrio"
                  onClick={() => setConfirmar(true)}
                >
                  <Trash2 size={16} /> Borrar
                </button>
              )
            ) : (
              <span />
            )}
            <button type="submit" className="btn-cs btn-primario" disabled={ocupado}>
              {guardar.isPending ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Detalle de una etapa oficial del ENARM (solo lectura: la configura Consalud). */
export function EtapaEnarmDialog({ etapa, onClose }: { etapa: EventoEnarm; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent>
        <p className="text-xs font-extrabold tracking-wide text-verde-texto uppercase">
          Fecha oficial ENARM
        </p>
        <DialogTitle className="mt-1">{etapa.title}</DialogTitle>
        <DialogDescription className="flex items-center gap-1.5">
          <CalendarClock size={16} className="shrink-0" />
          <span className="first-letter:uppercase">
            {rango(etapa.date, etapa.endDate)}
            {etapa.timeNote ? ` · ${etapa.timeNote}` : ''}
          </span>
        </DialogDescription>
        {etapa.description && (
          <p className="mt-4 text-sm whitespace-pre-line text-texto-2">{etapa.description}</p>
        )}
        {etapa.sourceUrl && (
          <a
            href={etapa.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cs btn-contorno mt-5"
          >
            <ExternalLink size={16} /> Ver fuente oficial
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
}
